/**
 * Turning browser notifications on and off.
 *
 * Three parties have to agree: the browser (permission), the push service (a subscription),
 * and this server (the subscription stored against the signed-in person). Any of them can
 * be missing, which is what `PushState` distinguishes — a button that only says "on/off"
 * leaves people stuck when the browser itself has blocked notifications.
 */

import { request } from '../api/client'

export type PushState =
  /** No service worker or no Push API — an old browser, or Safari without "Add to Home Screen". */
  | 'unsupported'
  /** The server has no VAPID keys, so nothing can be sent. */
  | 'disabled'
  /** The person blocked notifications in the browser; only they can undo that. */
  | 'denied'
  | 'off'
  | 'on'

export function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register('/sw.js')
}

/** What the button should show, without asking for anything. */
export async function currentState(enabledOnServer: boolean): Promise<PushState> {
  if (!isSupported()) return 'unsupported'
  if (!enabledOnServer) return 'disabled'
  if (Notification.permission === 'denied') return 'denied'
  try {
    const existing = await (await registration()).pushManager.getSubscription()
    return existing ? 'on' : 'off'
  } catch {
    return 'off'
  }
}

/** The VAPID key travels as base64url text and has to reach the browser as bytes. */
function keyToBytes(base64url: string): ArrayBuffer {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index)
  return bytes.buffer
}

/** Ask for permission, subscribe, and register the subscription here. */
export async function enable(publicKey: string): Promise<PushState> {
  if (!isSupported()) return 'unsupported'
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'

  const reg = await registration()
  await navigator.serviceWorker.ready
  const subscription =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      // Required by every browser: a push must always result in something visible.
      userVisibleOnly: true,
      applicationServerKey: keyToBytes(publicKey),
    }))

  const raw = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> }
  await request('/push/subscriptions', {
    method: 'POST',
    body: JSON.stringify({ endpoint: subscription.endpoint, keys: raw.keys ?? null }),
  })
  return 'on'
}

/** Forget this browser, here and at the push service. */
export async function disable(): Promise<PushState> {
  if (!isSupported()) return 'unsupported'
  const subscription = await (await registration()).pushManager.getSubscription()
  if (!subscription) return 'off'
  await request('/push/subscriptions', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  }).catch(() => undefined)
  await subscription.unsubscribe()
  return 'off'
}
